"use client"

import Link from "next/link"
import { useRef, useState, type KeyboardEvent, type ReactNode } from "react"

import { PayOverTime } from "@/components/marketing/pay-over-time"

/** One row of a card's billing picker. */
export type BillingPickerOption = {
  /** The row's key. The card carries the selected one as `data-sel`. */
  value: string
  /** The left side of the row: its name and, on a discounted row, its tag. */
  label: ReactNode
  /** The right side of the row: the price and its unit or rate line. */
  price: ReactNode
  /** The checkout this row buys. The one Join button follows the selection. */
  href: string
  /** A ONE-TIME amount in cents. When set, the providers' own pay-over-time
   *  lines mount under the button while this row is selected. A recurring
   *  price leaves it unset, since the pay-over-time rails cannot take one. */
  payOverTimeCents?: number
  /** The terms line under the button while this row is selected. Every row's
   *  line is rendered and only the selected one shows, so each is in the page
   *  as written. Phrasing content only: it renders inside a <p>. */
  terms?: ReactNode
  /** The row's own sentence, shown under the picker while this row is
   *  selected, rendered and hidden the same way as `terms`. */
  detail?: ReactNode
  /** The Join button's label while this row is selected, in place of the
   *  card's shared `button`, so the button names the price it buys. */
  buttonLabel?: ReactNode
}

/**
 * A pricing card whose price slot is a set of selectable rows, and whose ONE
 * Join button buys the row selected. It holds only the selection: the card's
 * words, the rows' prices and the checkout each row opens all come from the
 * server half in instances.tsx, read from PLAN_CONFIG there.
 *
 * `prefix` is the cell's class prefix (`odmp`, `cprc`), so every class here
 * resolves to that cell's own self-contained CSS and the card renders the same
 * on its page and in the module workbench.
 *
 * The first render is the default row on the server and the client alike, so
 * a buyer without JavaScript still gets a working button for that row.
 *
 * The rows are one radio group for the keyboard, the WAI-ARIA radio-group
 * pattern: only the selected row is a Tab stop, and the arrow keys (with Home
 * and End) move the selection and the focus together, wrapping at the ends.
 * Space and Enter on a focused row select it, which the button gives for free.
 */
export function BillingPickerCard({
  prefix,
  ariaLabel,
  defaultValue,
  options,
  head,
  body,
  button,
}: {
  prefix: string
  ariaLabel: string
  defaultValue: string
  options: BillingPickerOption[]
  /** Everything above the picker (the belt mark and the name). */
  head: ReactNode
  /** Everything between the picker and the button (who it is for, the list). */
  body: ReactNode
  /** The Join button's label. */
  button: ReactNode
}) {
  const [value, setValue] = useState(defaultValue)
  const selected = options.find((option) => option.value === value) ?? options[0]
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([])

  function onRowKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = options.length - 1
    let next: number | null = null
    if (event.key === "ArrowDown" || event.key === "ArrowRight") next = index === last ? 0 : index + 1
    else if (event.key === "ArrowUp" || event.key === "ArrowLeft") next = index === 0 ? last : index - 1
    else if (event.key === "Home") next = 0
    else if (event.key === "End") next = last
    if (next === null) return
    event.preventDefault()
    setValue(options[next].value)
    rowRefs.current[next]?.focus()
  }

  return (
    <div className={`${prefix}-card`} data-sel={selected.value}>
      {head}
      <div className={`${prefix}-pick`} role="radiogroup" aria-label={ariaLabel}>
        {options.map((option, index) => (
          <button
            key={option.value}
            ref={(node) => {
              rowRefs.current[index] = node
            }}
            type="button"
            className={`${prefix}-popt`}
            role="radio"
            aria-checked={option.value === selected.value}
            tabIndex={option.value === selected.value ? 0 : -1}
            onClick={() => setValue(option.value)}
            onKeyDown={(event) => onRowKeyDown(event, index)}
          >
            <span className={`${prefix}-radio`} />
            <span className={`${prefix}-plab`}>{option.label}</span>
            <span className={`${prefix}-pr`}>{option.price}</span>
          </button>
        ))}
      </div>
      {options.map((option) =>
        option.detail ? (
          <p key={option.value} className={`${prefix}-pdesc`} hidden={option.value !== selected.value}>
            {option.detail}
          </p>
        ) : null,
      )}
      {body}
      <Link className={`${prefix}-btn`} href={selected.href}>
        {selected.buttonLabel ?? button}
      </Link>
      {selected.payOverTimeCents ? (
        // Keyed on the row so the providers re-quote at the new amount.
        <div className={`${prefix}-foot`}>
          <PayOverTime key={selected.value} amountCents={selected.payOverTimeCents} />
        </div>
      ) : null}
      {options.map((option) =>
        option.terms ? (
          <p key={option.value} className={`${prefix}-terms`} hidden={option.value !== selected.value}>
            {option.terms}
          </p>
        ) : null,
      )}
    </div>
  )
}
